/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Editor, Transforms } from "slate";
import { jsx } from "slate-hyperscript";

import { CustomElement } from "src/declare";

const ELEMENT_TAGS: any = {
  BLOCKQUOTE: () => ({ type: "quote" }),
  H1: () => ({ type: "plain" }),
  H2: () => ({ type: "plain" }),
  H3: () => ({ type: "plain" }),
  H4: () => ({ type: "plain" }),
  H5: () => ({ type: "plain" }),
  H6: () => ({ type: "plain" }),
  IMG: (el: any) => ({ type: "image", url: el.getAttribute("src") }),
  LI: () => ({ type: "list-item" }),
  OL: () => ({ type: "numbered-list" }),
  P: () => ({ type: "plain" }),
  PRE: () => ({ type: "code" }),
  UL: () => ({ type: "bulleted-list" }),
  DIV: () => ({ type: "plain" }),
  TR: () => ({ type: "plain" }),
};

const TEXT_TAGS: any = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
  A: (el: any) => ({ link: el.getAttribute("href") }),
};

const deserialize = (el: ChildNode): any => {
  // nodeType 3 is a text node
  if (el.nodeType === 3) {
    return el.textContent;
    // nodeType 1 is an element
  } else if (el.nodeType !== 1) {
    return null;
  } else if (el.nodeName === "BR") {
    return "\n";
  }

  const { nodeName } = el;
  let parent = el;

  if (nodeName === "PRE" && el.childNodes[0] && el.childNodes[0].nodeName === "CODE") {
    parent = el.childNodes[0];
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat();
  // stop children being empty and causing bugs
  if (children.length === 0) {
    children = [{ text: "" }];
  }

  if (el.nodeName === "BODY") {
    return jsx("fragment", {}, children);
  }

  // look through the element tags and check if the element is one of them
  if (ELEMENT_TAGS[nodeName]) {
    // if it is, set the attributes to the attributes of the element
    const attrs = ELEMENT_TAGS[nodeName](el);
    // get the font if it exists
    const details = getDetails(el);
    // create an element of the appropriate type
    const element = jsx("element", attrs, children);
    // if the font exists, set the font of the element using the setFont function
    if (details) element.children = setFontDetails(details, element.children);
    // if any of the children has the type plain, move them up the tree (plain should be the first child)
    element.children = element.children
      .map((child: any) => {
        if (child.type === "plain") {
          return child.children;
        }
        return child;
      })
      .flat();
    // return the element's children
    return element.children;
  }

  // look through the text tags and check if the element is one of them
  if (TEXT_TAGS[nodeName]) {
    // if it is, set the attributes to the attributes of the element
    const attrs = TEXT_TAGS[nodeName](el);
    // return with the formatting applied to the text, not the element
    return children.map((child) => format(attrs, child));
  }

  const details = getDetails(el);
  if (details) {
    const text = children.map((child) => {
      if (typeof child === "string") return { ...jsx("text", {}, child), ...details };
      if (child.text || child.text === "") return { ...child, ...details };
      return child;
    });
    return text;
  }
  return children;
};

/**
 * A function to get font details stored in the element's attributes, rather than as a tag
 */
const getDetails = (el: any) => {
  const font = el.style.fontFamily;
  const fontWeight = el.style.fontWeight;
  if (font && (fontWeight === "bold" || fontWeight === "bolder" || fontWeight >= 600)) {
    return { font, bold: true };
  } else if (font) {
    return { font };
  } else if (fontWeight === "bold" || fontWeight === "bolder" || fontWeight >= 600) {
    return { fontWeight: true };
  }
  return;
};

/**
 * A function to set font details (font type, bold, etc...) of the text in an elemnt
 */
const setFontDetails = (font: any, children: any): any => {
  // loop through each child
  return children.map((child: any) => {
    // if the child has children, recursively call the function on the child
    // this ensures the font is only ever set on the text of the element
    if (child.children) {
      return { ...child, children: setFontDetails(font, child.children) };
    }
    // otherwise if the child is a text element, set the font
    return { ...child, ...font };
  });
};

/**
 * A function to add the formatting to the text of an element (bold, italic, etc)
 */
const format = (attrs: any, child: any): any => {
  // if the child has children, recursively call the function on the child
  // this ensures the formatting is only ever applied to the text of the element
  if (child.children) {
    const url = child.url;
    const type = child.type;
    return child.children.map((childOfChild: any) =>
      jsx("fragment", { type: type, url: url }, format(attrs, childOfChild))
    );
  }
  // otherwise if the child is a text element, add the formatting
  return jsx("text", attrs, child.text || child);
};

export const withHtml = (editor: Editor) => {
  const { insertData, isInline } = editor;

  editor.isInline = (element) => {
    return (element as CustomElement).type === "link" ? true : isInline(element);
  };

  editor.insertData = (data) => {
    const catchSlateFragment = /data-slate-fragment="(.+?)"/m;
    const html = data.getData("text/html");

    // fragment handles the case in which the user pastes a fragment of Slate content, as this is stored wierdly in the HTML
    const [, fragment] = html.match(catchSlateFragment) || [];

    // if the data is html, we need to parse it, deserialize it, and insert it
    if (html && !fragment) {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const fragments = deserialize(parsed.body);
      try {
        Transforms.insertNodes(editor, fragments, checkListInFragment(fragments));
      } catch {
        Transforms.insertFragment(editor, fragments);
      }
      checkListItems(editor);
      return;
    }

    // otherwise, just insert the data as plain text
    insertData(data);
    checkListItems(editor);
  };
  return editor;
};

const checkListInFragment = (fragment: any) => {
  // a function to stop list items containing another list
  // for each item in the fragment
  return fragment.map((parent: any) => {
    // if the item is a list
    if (parent.type === "bulleted-list" || parent.type === "numbered-list") {
      // call this function recursivelly on the children of the list
      return { type: parent.type, children: checkListInFragment(parent.children).flat() };
    } else if (parent.type === "list-item") {
      // if the item is a list item, check if it contains a list
      const newParent: any = [];
      parent.children
        .map((child: any) => {
          // if it does contain a list, return the list
          if (child.type === "bulleted-list" || child.type === "numbered-list") {
            // call the function recursivelly on the children of the list
            newParent.push({ type: child.type, children: checkListInFragment(child.children).flat() });
          } else if (
            newParent.length &&
            newParent[newParent.length - 1].type === "list-item" &&
            child.type !== "list-item"
          ) {
            // if there is a previous list item to add text to, add the text to the previous list item
            newParent[newParent.length - 1].children.push(child);
          } else {
            // otherwise, add the text to a new list item
            newParent.push({ type: "list-item", children: [child] });
          }
        })
        .flat();
      return newParent;
    } else if (parent.children) {
      return { ...parent, children: checkListInFragment(parent.children).flat() };
    } else {
      // otherwise, we are looking at text so return the item
      return parent;
    }
  });
};

/**
 * This function turns any plain text in lists into list items. This is needed as after
 * pasting text into a list, the text is not turned into a list item.
 */
const checkListItems = (editor: Editor) => {
  // for every plain text node in the editor
  const nodes = Editor.nodes(editor, {
    at: [],
    match: (n: any) => n.type === "plain",
  });
  // set the initial value of the node
  let node = nodes.next();
  // while there are nodes to check
  while (!node.done) {
    // if the path length of the node is greater than 1, we must be in a list
    if (node.value[1].length > 1) {
      // set the node type to list-item
      Transforms.setNodes(editor, { type: "list-item" }, { at: node.value[1] });
    }
    // move to the next node
    node = nodes.next();
  }
};
